import React, { useState } from "react";
import styled from "@emotion/styled";

export enum AnimationType {
	None = "none",
	Opacity = "opacity",
	Scale = "scale",
}

interface AnimatedButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
	children: React.ReactNode;
	type?: AnimationType;
	isActive?: boolean;
}

export const AnimatedButton = React.forwardRef<HTMLButtonElement, AnimatedButtonProps>(
	({ type = AnimationType.Opacity, children, onMouseDown, onMouseUp, onTouchStart, onTouchEnd, style, ...props }, ref) => {
		const [isPressed, setIsPressed] = useState(false);

		const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
			setIsPressed(true);
			onMouseDown?.(e);
		};

		const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
			setIsPressed(false);
			onMouseUp?.(e);
		};

		const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
			setIsPressed(true);
			onTouchStart?.(e);
		};

		const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
			setIsPressed(false);
			onTouchEnd?.(e);
		};

		return (
			<StyledAnimatedButton
				ref={ref}
				animationType={type}
				isPressed={isPressed}
				onMouseDown={handleMouseDown}
				onMouseUp={handleMouseUp}
				onMouseLeave={() => setIsPressed(false)}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				style={style}
				{...props}
			>
				{children}
			</StyledAnimatedButton>
		);
	}
);

AnimatedButton.displayName = "AnimatedButton";

const StyledAnimatedButton = styled.button<{ animationType: AnimationType; isPressed: boolean }>`
	transition: all 0.15s ease-out;

	${({ animationType, isPressed }) => {
		switch (animationType) {
			case AnimationType.Opacity:
				return `
					opacity: ${isPressed ? 0.8 : 1};
				`;
			case AnimationType.Scale:
				return `
					transform: scale(${isPressed ? 0.95 : 1});
				`;
			case AnimationType.None:
			default:
				return "";
		}
	}}
`;
